import { useEffect, useState, useContext } from 'react';
import AuthContext from '../../../contexts/auth/AuthContext';
import UIContext from '../../../contexts/ui/UIContext';
import ProcessContext from '../../../contexts/process/ProcessContext';
import Card from './Card';
import Create from './Create';
import Loading from '../../ui-elements/Loading';
import StatusBarButton from '../../layout/StatusBarButton';
import { useNavigate } from 'react-router-dom';
import UsersApiService from '../../../services/apiServices/UsersApiService';
import TenantsApiService from '../../../services/apiServices/TenantsApiService';
import { HELP_DESK_LINK } from '../../../constants/helpDesk';

const List = () => {
    const [adding, setAdding] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tenantUsers, setTenantUsers] = useState(null);
    const { currentTenant } = useContext(AuthContext);
    const { setInfo } = useContext(UIContext);
    const { notifySuccess, notifyError } = useContext(ProcessContext);
    const navigate = useNavigate();

    useEffect(() => {
        setInfo('Tenant User Management');

        const initialLoad = async () => {
            await load();
        };

        initialLoad();
    }, []);

    const toggleAdding = () => {
        setAdding(!adding);
    };

    const load = async () => {
        try {
            setLoading(true);
            const users = (await TenantsApiService.getUsersOfTenant(currentTenant.id)).data.data;
            setTenantUsers(
                users.sort(function (a, b) {
                    if (!a.familyName && !b.familyName) {
                        return 0;
                    }
                    else if (!a.familyName) {
                        return -1;
                    }
                    else if (!b.familyName) {
                        return 1;
                    }
                    else {
                        let x = a.familyName.toUpperCase(),
                            y = b.familyName.toUpperCase();
                        return x == y ? 0 : x > y ? 1 : -1;
                    }
                })
            );
        } catch (err) {
            console.error('Error while getting user ids for tenant: ', err);
        } finally {
            setLoading(false);
        }
    };

    const createUser = async (user) => {
        setLoading(true);
        user.email = user.email.replace(/\s+/g, '');
        try {
            const userId = await _getOrCreateUserAsync(user);
            const body = {
                userId,
                roles: user.roles
            };
            await TenantsApiService.addUserToTenant(currentTenant.id, body);
            notifySuccess('Successfully added user to tenant.');
        } catch (error) {
            if (error.message === 'Not found') {
                notifyError(<>We have encountered an issue while saving the new tenant user. Could you please logout, sign back in, and try again? If the error still exists, please contact the support team {HELP_DESK_LINK}.</>);
            }
            else {
                notifyError(<>We have encountered an issue on our side while saving the new tenant user. Please contact the support team {HELP_DESK_LINK}.</>);
            }
        }
        finally {
            toggleAdding();
            await load();
        }
    };

    const _getOrCreateUserAsync = async (tenantUser) => {
        const body = {
            email: tenantUser.email,
            isGlobalAdmin: false,
        };

        const addUserResult = await UsersApiService.addUser(body);
        return addUserResult?.data.data.id;
    };

    const deleteHandler = async (userId) => {
        setLoading(true);
        await TenantsApiService.deleteUserFromTenant(currentTenant.id, userId)
            .then(() => {
                notifySuccess("User was successfully deleted.")
            })
            .catch((error) => {
                if (error.message === 'Not found') {
                    notifyError(<>We have encountered an issue while deleting the user. Could you please logout, sign back in, and try again? If the error still exists, please contact the support team {HELP_DESK_LINK}.</>);
                }
                else {
                    notifyError(<>We have encountered an issue on our side while deleting the tenant. Please contact the support team {HELP_DESK_LINK}.</>);
                }
                console.log(error.message);
            })
            .finally(() => {
                return load();
            });
    };

    return loading ? <Loading fullScreen /> : (
        <div className="tenant-user-management-container">
            <div className="tenant-user-management-list">
                {!adding && (
                    <div className="tenant-user-management-adding" onClick={toggleAdding}>
                        <i className="fas fa-plus"></i>
                    </div>)}
                {adding && (
                    <Create
                        cancelHandler={toggleAdding}
                        createHandler={createUser}
                    />
                )}
                {tenantUsers?.length > 0 &&
                    tenantUsers?.map((user) => (
                        <Card
                            tenantUser={user}
                            handleDelete={deleteHandler}
                            key={user.id}
                        />))}
                <StatusBarButton
                    label='Done'
                    clickHandler={() => navigate('/')}
                    statusSlot={3}
                    icon='fair fai-circle-check'
                    type='inline'
                />
            </div>
        </div>
    );
};

export default List;
